import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await signup(name, email, password); toast.success("Bienvenue chez Local Pulse !"); nav("/dashboard"); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur d'inscription"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="signup-page">
      <div className="hidden md:flex md:w-1/2 relative items-center justify-center p-12 overflow-hidden border-r border-border">
        <div className="aurora opacity-80" />
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative max-w-md fade-up">
          <Link to="/"><Logo size={26} /></Link>
          <h2 className="mt-12 text-4xl lg:text-5xl font-black tracking-tighter font-display leading-tight">
            Démarrez votre<br />
            <span className="text-gradient">croissance locale</span><br />
            <span className="font-serif-italic font-normal">en 2 minutes.</span>
          </h2>
          <ul className="mt-10 space-y-3 text-sm">
            {[
              "Profil business sans limites",
              "Diagnostic complet de votre visibilité",
              "Premier contenu rédigé en 30 secondes",
              "Aucune carte bancaire requise",
            ].map(t => (
              <li key={t} className="flex items-center gap-3">
                <span className="h-5 w-5 rounded-full bg-primary/15 flex items-center justify-center"><Check className="h-3 w-3 text-primary" /></span>
                {t}
              </li>
            ))}
          </ul>
          <div className="mt-12 grid grid-cols-3 gap-3 text-center">
            {[{ v: "FR", l: "Conçu en France" }, { v: "★★★★★", l: "Plans payants" }, { v: "RGPD", l: "Hébergement EU" }].map(s => (
              <div key={s.l} className="p-3 glass rounded-xl border border-border">
                <div className="font-display font-black text-xl">{s.v}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5 fade-up" data-testid="signup-form">
          <div className="md:hidden mb-6"><Link to="/"><Logo size={22} /></Link></div>
          <div>
            <h1 className="text-3xl font-black tracking-tighter font-display">Créer un compte</h1>
            <p className="text-sm text-muted-foreground mt-1.5">Démarrez gratuitement, sans carte bancaire.</p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-bold">Nom complet</Label>
            <Input required value={name} onChange={(e) => setName(e.target.value)} className="rounded-md h-11" placeholder="Jean Dupont" data-testid="signup-name-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-bold">Email</Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-md h-11" placeholder="vous@exemple.fr" data-testid="signup-email-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-bold">Mot de passe</Label>
            <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-md h-11" placeholder="6 caractères minimum" data-testid="signup-password-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full h-11 btn-glow" data-testid="signup-submit-btn">
            {loading ? "Création…" : "Créer mon compte"}<ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <p className="text-sm text-center text-muted-foreground">
            Déjà un compte ? <Link to="/login" className="text-primary hover:underline font-medium" data-testid="signup-login-link">Se connecter</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
